import React from 'react';
import CustomTooltip from '../Utils/CustomTooltip';

const EvaluationSettings = ({ formData, handleChange, handleStepChange, errors }) => {
  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-6">
        Evaluation Default Settings
      </h2>
      
      {/* Two Column Layout for Questionnaire and Challenge */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Questionnaire Column */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Questionnaire</h3>
          
          {/* Target Questionnaire Questions */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label 
                htmlFor="target_questionnaire_questions" 
                className="block text-sm font-medium text-gray-700"
              >
                Target Questions
              </label>
              <CustomTooltip content="Number of questionnaire questions to generate for evaluations." position="top" minWidth="200px">
                <button 
                  type="button"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Show help"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </button>
              </CustomTooltip>
            </div>
            <input
              type="number"
              id="target_questionnaire_questions"
              name="target_questionnaire_questions"
              value={formData.target_questionnaire_questions}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md ${errors.target_questionnaire_questions ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="10"
              min="1"
              max="50"
            />
            {errors.target_questionnaire_questions && (
              <p className="mt-1 text-sm text-red-600">{errors.target_questionnaire_questions}</p>
            )}
          </div>

          {/* Target Questionnaire Time */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label 
                htmlFor="target_questionnaire_time" 
                className="block text-sm font-medium text-gray-700"
              >
                Target Time
                <span className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded">Coming Soon</span>
              </label>
              <CustomTooltip content="Create questionnaire questions that can be completed within this time limit (in minutes)." position="top" minWidth="250px">
                <button 
                  type="button"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Show help"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </button>
              </CustomTooltip>
            </div>
            <div className="relative">
              <input
                type="number"
                id="target_questionnaire_time"
                name="target_questionnaire_time"
                value={formData.target_questionnaire_time}
                onChange={handleChange}
                disabled={true}
                className="w-full p-2 pr-16 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                placeholder="20"
                min="1"
                max="180"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                minutes
              </span>
            </div>
            {errors.target_questionnaire_time && (
              <p className="mt-1 text-sm text-red-600">{errors.target_questionnaire_time}</p>
            )}
          </div>

          {/* Target Questionnaire Breadth */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label 
                htmlFor="target_questionnaire_breadth" 
                className="block text-sm font-medium text-gray-700"
              >
                Breadth
                <span className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded">Coming Soon</span>
              </label>
              <CustomTooltip content="Breadth level for questionnaire questions (feature coming soon)." position="top" minWidth="200px">
                <button 
                  type="button"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Show help"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </button>
              </CustomTooltip>
            </div>
            <select
              id="target_questionnaire_breadth"
              name="target_questionnaire_breadth"
              value={formData.target_questionnaire_breadth}
              onChange={handleChange}
              disabled={true}
              className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
            >
              <option value="">Select breadth level</option>
              <option value="Mixed">Mixed</option>
              <option value="Focused">Focused</option>
              <option value="Involved">Involved</option>
              <option value="Comprehensive">Comprehensive</option>
            </select>
          </div>
        </div>

        {/* Challenge Column */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Challenge</h3>
          
          {/* Target Challenge Questions */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label 
                htmlFor="target_challenge_questions" 
                className="block text-sm font-medium text-gray-700"
              >
                Target Questions
              </label>
              <CustomTooltip content="Number of challenge questions to generate for evaluations." position="top" minWidth="200px">
                <button 
                  type="button"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Show help"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </button>
              </CustomTooltip>
            </div>
            <input
              type="number"
              id="target_challenge_questions"
              name="target_challenge_questions"
              value={formData.target_challenge_questions}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md ${errors.target_challenge_questions ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="10"
              min="1"
              max="20"
            />
            {errors.target_challenge_questions && (
              <p className="mt-1 text-sm text-red-600">{errors.target_challenge_questions}</p>
            )}
          </div>

          {/* Target Challenge Time */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label 
                htmlFor="target_challenge_time" 
                className="block text-sm font-medium text-gray-700"
              >
                Target Time
              </label>
              <CustomTooltip content="Create challenge questions that can be completed within this time limit (in minutes)." position="top" minWidth="250px">
                <button 
                  type="button"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Show help"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </button>
              </CustomTooltip>
            </div>
            <div className="relative">
              <input
                type="number"
                id="target_challenge_time"
                name="target_challenge_time"
                value={formData.target_challenge_time}
                onChange={handleChange}
                className={`w-full p-2 pr-16 border rounded-md ${errors.target_challenge_time ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="40"
                min="1"
                max="300"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                minutes
              </span>
            </div>
            {errors.target_challenge_time && (
              <p className="mt-1 text-sm text-red-600">{errors.target_challenge_time}</p>
            )}
          </div>

          {/* Target Challenge Breadth */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label 
                htmlFor="target_challenge_breadth" 
                className="block text-sm font-medium text-gray-700"
              >
                Breadth
              </label>
              <CustomTooltip content="The Breadth determines the scope and complexity of the challenge questions." position="top" minWidth="200px">
                <button 
                  type="button"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Show help"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </button>
              </CustomTooltip>
            </div>
            <select
              id="target_challenge_breadth"
              name="target_challenge_breadth"
              value={formData.target_challenge_breadth || ""}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md ${errors.target_challenge_breadth ? 'border-red-500' : 'border-gray-300'}`}
            >
              <option value="">Select breadth level</option>
              <option value="Mixed">Mixed</option>
              <option value="Focused">Focused</option>
              <option value="Involved">Involved</option>
              <option value="Comprehensive">Comprehensive</option>
            </select>
            {errors.target_challenge_breadth && (
              <p className="mt-1 text-sm text-red-600">{errors.target_challenge_breadth}</p>
            )}
          </div>

          {/* Challenge Type */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label 
                htmlFor="challenge_type" 
                className="block text-sm font-medium text-gray-700"
              >
                Challenge Type
              </label>
              <CustomTooltip content="The type of challenge format (Quiz or Interview style)." position="top" minWidth="200px">
                <button 
                  type="button"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Show help"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </button>
              </CustomTooltip>
            </div>
            <select
              id="challenge_type"
              name="challenge_type"
              value={formData.challenge_type || "quiz"}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md ${errors.challenge_type ? 'border-red-500' : 'border-gray-300'}`}
            >
              <option value="">Select challenge type</option>
              <option value="quiz">Quiz</option>
              <option value="interview">Interview</option>
            </select>
            {errors.challenge_type && (
              <p className="mt-1 text-sm text-red-600">{errors.challenge_type}</p>
            )}
          </div>
        </div>
      </div>

      {/* Countdown Enabled Default */}
      <div className="mb-6">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="countdown_enabled"
            name="countdown_enabled"
            checked={formData.countdown_enabled || false}
            onChange={(e) => handleChange({ target: { name: 'countdown_enabled', value: e.target.checked } })}
            className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
          <label htmlFor="countdown_enabled" className="ml-3 text-sm font-medium text-gray-700">
            Countdown Enabled Default
          </label>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          When enabled, new evaluations will have countdown enabled by default.
        </p>
      </div>

      {/* Evaluation Steps Section */}
      <div className="mb-6">
        <h3 className="text-base font-medium text-gray-700 mb-3">Evaluation Steps</h3>
        <p className="text-sm text-gray-500 mb-4">
          Configure which steps are enabled by default for new evaluations.
        </p>
        <div className="space-y-3 text-sm">
          <div className="flex items-center opacity-50 pointer-events-none">
            <input
              type="checkbox"
              id="resume"
              checked={formData.evaluation_steps?.resume || false}
              disabled
              className="w-4 h-4 text-gray-400 bg-gray-100 border-2 border-gray-300 rounded mr-3 cursor-not-allowed"
            />
            <label htmlFor="resume" className="text-sm text-gray-400 cursor-not-allowed">
              Ask for Resume
            </label>
          </div>
          <div className="flex items-center opacity-50 pointer-events-none">
            <input
              type="checkbox"
              id="kyc"
              checked={formData.evaluation_steps?.kyc || false}
              disabled
              className="w-4 h-4 text-gray-400 bg-gray-100 border-2 border-gray-300 rounded mr-3 cursor-not-allowed"
            />
            <label htmlFor="kyc" className="text-sm text-gray-400 cursor-not-allowed">
              Identity Verification (KYC)
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="video_intro"
              checked={formData.evaluation_steps?.video_intro || false}
              onChange={(e) => handleStepChange('video_intro', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded mr-3 focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="video_intro" className="text-sm text-gray-700">
              Video Intro
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="questionnaire"
              checked={formData.evaluation_steps?.questionnaire !== undefined ? formData.evaluation_steps.questionnaire : true}
              onChange={(e) => handleStepChange('questionnaire', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded mr-3 focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="questionnaire" className="text-sm text-gray-700">
              Questionnaire
            </label>
          </div>
          <div className="flex items-center opacity-50 pointer-events-none">
            <input
              type="checkbox"
              id="challenge"
              checked={formData.evaluation_steps?.challenge || false}
              disabled
              className="w-4 h-4 text-gray-400 bg-gray-100 border-2 border-gray-300 rounded mr-3 cursor-not-allowed"
            />
            <label htmlFor="challenge" className="text-sm text-gray-400 cursor-not-allowed">
              Practical Challenge
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationSettings;
